import type { TFunction } from 'i18next';

/**
 * Tours de coupe (table cup_rounds) et libellé « tour / journée » d'un match.
 * Un match de coupe se lit par son tour (matches.cup_round), jamais par son
 * game_day (presque toujours 1). Spec :
 * docs/superpowers/specs/2026-10-16-coupe-algerie-cup-rounds-design.md.
 */

/** Une ligne de public.cup_rounds. */
export interface CupRound {
  code: string;
  sortOrder: number;
  name: string;
  nameAr: string;
  shortName: string;
  shortNameAr: string;
}

/**
 * Match de coupe = match externe OU groupe de type 'cup'. Même règle que
 * isCupMatchRow (supabase/functions/_shared/disciplinary/cupMatches.ts, lignes
 * brutes) et que la fonction SQL public.is_cup_match.
 */
export function isCupMatch(m: {
  isExternalCompetition?: boolean | null;
  leagueGroup?: { kind?: string | null } | null;
}): boolean {
  return !!m.isExternalCompetition || m.leagueGroup?.kind === 'cup';
}

/**
 * Ligne brute d'une RPC / fonction edge (get_sanctions_details, get-disciplinary-history) :
 * son verdict serveur `is_cup` (public.is_cup_match) quand elle le porte ; sinon (fonction
 * pas encore redéployée), la règle d'isCupMatch sur ce que la ligne porte.
 */
export function isCupRow(row: {
  is_cup?: boolean | null;
  is_external?: boolean | null;
  is_external_competition?: boolean | null;
  league_group_kind?: string | null;
} | null | undefined): boolean {
  if (!row) return false;
  if (typeof row.is_cup === 'boolean') return row.is_cup;
  return isCupMatch({
    isExternalCompetition: row.is_external_competition ?? row.is_external,
    leagueGroup: row.league_group_kind ? { kind: row.league_group_kind } : null,
  });
}

/** Libellé d'un tour ; null si le code est vide ou absent de la table. */
export function cupRoundLabel(
  code: string | null | undefined,
  rounds: ReadonlyMap<string, CupRound>,
  lang: 'fr' | 'ar',
  variant: 'full' | 'short' = 'full',
): string | null {
  if (!code) return null;
  const round = rounds.get(code);
  if (!round) return null;
  if (variant === 'short') return lang === 'ar' ? round.shortNameAr : round.shortName;
  return lang === 'ar' ? round.nameAr : round.name;
}

/** Ce qu'il faut d'un match pour le libeller (Match ou forme partielle). */
export interface RoundLabelMatch {
  isExternalCompetition?: boolean | null;
  leagueGroup?: { kind?: string | null } | null;
  cupRound?: string | null;
  gameDay?: number | null;
}

/**
 * « Demi-finale » pour un match de coupe (« Tour non précisé » si son tour est
 * inconnu — jamais « Journée 1 »), « Journée 4 » / « J4 » pour un match de championnat.
 * `t` doit être dans la langue `lang`.
 */
export function matchRoundLabel(
  m: RoundLabelMatch,
  rounds: ReadonlyMap<string, CupRound>,
  lang: 'fr' | 'ar',
  t: TFunction,
  variant: 'full' | 'short' = 'full',
): string {
  if (isCupMatch(m)) {
    return cupRoundLabel(m.cupRound, rounds, lang, variant) ?? t('common:cup.round_unknown');
  }
  const n = m.gameDay ?? '';
  return variant === 'short' ? t('common:cup.game_day_short', { n }) : t('common:cup.game_day', { n });
}

/** Clé de tri d'un match de coupe : sort_order de son tour, 0 si inconnu. */
export function cupRoundSortKey(
  m: { cupRound?: string | null },
  rounds: ReadonlyMap<string, CupRound>,
): number {
  if (!m.cupRound) return 0;
  return rounds.get(m.cupRound)?.sortOrder ?? 0;
}

const SENIOR_CATEGORY_CODE = 'SENIOR';

/**
 * Noms d'un groupe de la ligue EXT_COMPETITIONS (une Coupe d'Algérie par saison et
 * catégorie) : « Coupe d'Algérie 2026-2027 », « Coupe d'Algérie U20 2026-2027 » /
 * « كأس الجزائر أقل من 20 سنة 2026-2027 ». Séniors sans suffixe ; nom de catégorie
 * vide → son code.
 */
export function cupCompetitionNames(
  category: { code: string; name_fr: string; name_ar: string },
  season: string,
): { name: string; name_ar: string } {
  if (category.code === SENIOR_CATEGORY_CODE) {
    return { name: `Coupe d'Algérie ${season}`, name_ar: `كأس الجزائر ${season}` };
  }
  const fr = category.name_fr?.trim() || category.code;
  const ar = category.name_ar?.trim() || category.code;
  return { name: `Coupe d'Algérie ${fr} ${season}`, name_ar: `كأس الجزائر ${ar} ${season}` };
}
